import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import AuthenticatedLayout from './App';
import { AuthCallbackPage } from './pages/auth/callback';

export interface RouteMeta {
  title?: string;
  breadcrumb?: string;
  requiresAuth?: boolean;
}

type AppRoute = RouteObject & {
  meta?: RouteMeta;
};

// Lazy load pages
const LandingPage = lazy(() => import('./pages/landing'));
const LoginPage = lazy(() => import('./pages/login'));
const SignUpPage = lazy(() => import('./pages/signup'));
const DashboardPage = lazy(() => import('./pages/dashboard/dashboardV2'));
const SettingsPage = lazy(() => import('./pages/settings'));
const ChatPage = lazy(() => import('./pages/chat'));
const TaskExamplePage = lazy(() => import('./pages/examples/TaskExample'));
const CalendarFlowExample = lazy(
  () => import('./pages/examples/CalendarFlowExample')
);
const PoliciesPage = lazy(() => import('./pages/policies'));
const PrivacyPolicyPage = lazy(() => import('./pages/policies/privacy-policy'));
const TermsAndConditionsPage = lazy(
  () => import('./pages/policies/terms-and-conditions')
);
const BlogListPage = lazy(() => import('./pages/blog/BlogList'));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPost'));

// Shared fallback for suspense
const LoadingFallback = () => <div>Loading...</div>;

// Default error route
export const errorElement = <Navigate to="/" replace />;

// Router config with meta support and v7 future flags
export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <LandingPage />
        </Suspense>
      ),
      meta: { title: 'Home', requiresAuth: false },
      errorElement,
    },
    {
      path: '/login',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <LoginPage />
        </Suspense>
      ),
      meta: { title: 'Login', requiresAuth: false },
    },
    {
      path: '/signup',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <SignUpPage />
        </Suspense>
      ),
      meta: { title: 'Sign Up', requiresAuth: false },
    },
    {
      path: '/auth/callback',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <AuthCallbackPage />
        </Suspense>
      ),
    },
    {
      path: '/policies',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <PoliciesPage />
        </Suspense>
      ),
      meta: { title: 'Policies', requiresAuth: false },
    },
    {
      path: '/policies/privacy-policy',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <PrivacyPolicyPage />
        </Suspense>
      ),
      meta: { title: 'Privacy Policy', requiresAuth: false },
    },
    {
      path: '/policies/terms-and-conditions',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <TermsAndConditionsPage />
        </Suspense>
      ),
      meta: { title: 'Terms & Conditions', requiresAuth: false },
    },
    // Blog routes (public)
    {
      path: '/blog',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <BlogListPage />
        </Suspense>
      ),
      meta: { title: 'Blog', requiresAuth: false },
    },
    {
      path: '/blog/:slug',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <BlogPostPage />
        </Suspense>
      ),
      meta: { title: 'Blog Post', requiresAuth: false },
    },
    {
      element: <AuthenticatedLayout />,
      meta: { requiresAuth: true },
      children: [
        {
          path: '/dashboard',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <DashboardPage />
            </Suspense>
          ),
          meta: {
            title: 'Dashboard',
            breadcrumb: 'Dashboard',
            requiresAuth: true,
          },
        },
        {
          path: '/settings',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SettingsPage />
            </Suspense>
          ),
          meta: {
            title: 'Settings',
            breadcrumb: 'Settings',
            requiresAuth: true,
          },
        },
        {
          path: '/chat',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ChatPage />
            </Suspense>
          ),
          meta: {
            title: 'Chat with Keysha',
            breadcrumb: 'Chat',
            requiresAuth: true,
          },
        },
        {
          path: '/tasks',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <TaskExamplePage />
            </Suspense>
          ),
          meta: { title: 'Tasks', breadcrumb: 'Tasks', requiresAuth: true },
        },
        {
          path: '/calendar-flow',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <CalendarFlowExample />
            </Suspense>
          ),
          meta: { title: 'Tasks', breadcrumb: 'Tasks', requiresAuth: true },
        },
      ],
      errorElement,
    },
    {
      path: '*',
      element: <Navigate to="/" replace />,
    },
  ] as AppRoute[],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
);

// TIPS
// Later, you can use useMatches() to read this metadata dynamically, for example:
// import { useMatches } from 'react-router-dom';

// export const PageTitle = () => {
//   const matches = useMatches();
//   const meta = matches[matches.length - 1]?.route?.meta;
//   return <h1>{meta?.title || 'App'}</h1>;
// };
